// Guests (spec/13 §3; spec/05 §3.4, F-SES-07): the venue's guest passes in use — sold at the counter to walk-ins without
// an account, each with the ticket code the guest types on a PC. One row per pass: its code, the pass, the time it has
// left, until when the code works, and where it is (waiting for payment, ready, playing on a PC). **Void** refunds the
// guest's pass through the existing pass refund (the money back to a tender, the time removed), which stops the code.
// Opened from the Floor's header; the list is the server's `GET …/guest-passes`.

import type { GuestPass, RefundPassRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, SelectField, Table, TextField, useToast, VisuallyHidden, type TableColumn } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { useStations } from '../../stations/queries';
import { Forbidden, Notice, Page, Section, TableSkeleton } from '../../ui';
import { useGuestPasses, useVoidGuestPass } from './queries';

import './guests.css';

const PASSES_READ = 'passes:read';
const PASSES_REFUND = 'passes:refund';

const REFUND_TENDERS = ['cash', 'counter_card', 'counter_upi'] as const;
type RefundTender = (typeof REFUND_TENDERS)[number];

export function GuestsPage(): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const canRead = access.status === 'ready' && access.can(PASSES_READ);
  const canVoid = access.status === 'ready' && access.can(PASSES_REFUND);
  const guests = useGuestPasses(venue.organizationId, venue.id, { enabled: canRead });
  const stations = useStations(venue.organizationId, venue.id);
  const [voiding, setVoiding] = useState<GuestPass | null>(null);

  const stationName = useMemo(() => {
    const byId = new Map<string, string>();
    for (const station of stations.data?.pages.flatMap((page) => page.data) ?? []) {
      byId.set(station.id, station.name ?? t('floor.tile.numbered', { number: station.number }));
    }
    return (id: string): string => byId.get(id) ?? t('guests.unknownStation');
  }, [stations.data, t]);

  if (access.status === 'ready' && !canRead) {
    return (
      <Page title={t('guests.title')}>
        <Forbidden message={t('guests.forbidden')} />
      </Page>
    );
  }

  const rows = guests.data?.guestPasses ?? [];
  const columns: readonly TableColumn<GuestPass>[] = [
    { key: 'code', header: t('guests.column.code'), cell: (row) => <span className="console-guest-code">{row.code}</span> },
    { key: 'pass', header: t('guests.column.pass'), cell: (row) => row.passName },
    { key: 'left', header: t('guests.column.left'), numeric: true, cell: (row) => (row.status === 'awaiting_payment' ? '—' : format.duration(row.remainingSeconds)) },
    {
      key: 'until',
      header: t('guests.column.until'),
      cell: (row) => (row.ticketExpiresAt === null ? '—' : format.dateTime(row.ticketExpiresAt, { date: 'medium', time: 'short' })),
    },
    {
      key: 'status',
      header: t('guests.column.status'),
      cell: (row) =>
        row.session !== null ? (
          <Badge tone="session" dot>
            {t('guests.status.playing', { station: stationName(row.session.stationId) })}
          </Badge>
        ) : (
          <Badge tone={row.status === 'active' ? 'ok' : 'neutral'} dot>
            {t(`guests.status.${row.status}`)}
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: <VisuallyHidden>{t('common.actions')}</VisuallyHidden>,
      cell: (row) =>
        canVoid && row.status === 'active' && row.holdingId !== null ? (
          <Button variant="secondary" size="sm" aria-label={t('guests.void.named', { code: row.code })} onClick={() => setVoiding(row)}>
            {t('guests.void.action')}
          </Button>
        ) : null,
    },
  ];

  let body: ReactNode;
  if (guests.isError) body = <LoadFailure error={guests.error} message={t('guests.unavailable')} onRetry={() => void guests.refetch()} />;
  else if (guests.data === undefined) body = <TableSkeleton rows={3} />;
  else if (rows.length === 0) {
    body = <EmptyState icon={<NavIcon name="passes" />} title={t('guests.emptyTitle')} description={t('guests.empty')} action={<Link className="clav-button clav-button--primary" to="/v/$venueSlug/sell" params={{ venueSlug: venue.slug }}>{t('guests.emptyAction')}</Link>} />;
  } else {
    body = (
      <div className="console-table-card">
        <Table caption={t('guests.title')} captionHidden columns={columns} rows={rows} rowKey={(row) => row.id} />
      </div>
    );
  }

  return (
    <Page title={t('guests.title')} description={t('guests.intro')}>
      {guests.data !== undefined && !guests.data.guestsEnabled ? <Notice role="note">{t('guests.off')}</Notice> : null}
      <Section body="rows">
        {body}
      </Section>
      <Dialog
        open={voiding !== null}
        onOpenChange={(open) => {
          if (!open) setVoiding(null);
        }}
        size="sm"
        dirty="auto"
        title={voiding === null ? '' : t('guests.void.title', { code: voiding.code })}
        description={t('guests.void.intro')}
      >
        {voiding === null ? null : <VoidGuestPassForm pass={voiding} organizationId={venue.organizationId} venueId={venue.id} onDone={() => setVoiding(null)} />}
      </Dialog>
    </Page>
  );
}

/** Void: the existing refund of the guest's pass, to a tender, with an optional reason (a refund override needs one). */
function VoidGuestPassForm({ pass, organizationId, venueId, onDone }: { readonly pass: GuestPass; readonly organizationId: string; readonly venueId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const refund = useVoidGuestPass(organizationId, venueId);
  const [tender, setTender] = useState<RefundTender>('cash');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pass.holdingId === null) return;
    setError(undefined);
    const body: RefundPassRequest = { tender, ...(reason.trim() === '' ? {} : { reason: reason.trim() }) };
    refund.mutate(
      { holdingId: pass.holdingId, body },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('guests.void.done', { code: pass.code }) });
          onDone();
        },
        onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.conflict || problemCode(mutationError) === 'pass_not_refundable' ? t('guests.void.notRefundable') : t('guests.void.failed')),
      },
    );
  }

  return (
    <form className="console-form" noValidate onSubmit={submit} aria-label={t('guests.void.title', { code: pass.code })}>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <SelectField
        label={t('guests.void.tender')}
        required
        options={REFUND_TENDERS.map((value) => ({ value, label: t(`passes.tender.${value}`) }))}
        value={tender}
        onValueChange={(value) => setTender(value as RefundTender)}
      />
      <TextField label={t('guests.void.reason')} showOptional value={reason} onChange={(event) => setReason(event.target.value)} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="submit" variant="primary" busy={refund.isPending}>
          {t('guests.void.confirm')}
        </Button>
      </div>
    </form>
  );
}
